<script lang="ts" setup>
import { useTranslations } from '~/composables/translations';
import JourneyCard from './HomepageJourneyCard.vue';
import HomepageBtn from './HomepageBtn.vue';
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import useI18NAsync from '~/composables/i18nAsync';

import { computed } from 'vue';
import type { HomepageJourneyCard } from '../../models/homepage';

const { $i18n } = useTranslations('components/homepage/uploadOwnDesign');
const { $i18n: $i18nPF } = useTranslations('process_families');
const i18nSuspense = useI18NAsync('components/homepage/uploadOwnDesign');
const i18nSuspenseCommon = useI18NAsync('common');

const journeys = computed(
  () =>
    [
      {
        name: $i18n('journeyBestPartner'),
        content: $i18n('journeyBestPartnerContent'),
        image: 'stageOfProject_map.jpg',
      },
      {
        name: $i18n('journeyFromDesignToPart'),
        content: $i18n('journeyFromDesignToPartContent'),
        image: 'stageOfProject_solidworks.jpg',
      },
      {
        name: $i18n('journeyOptimizeCollaboration'),
        content: $i18n('journeyOptimizeCollaborationContent'),
        image: 'stageOfProject_collaboration.jpg',
      },
      {
        name: $i18n('journeyEnsureTraceability'),
        content: $i18n('journeyEnsureTraceabilityContent'),
        image: 'stageOfProject_traceability.jpg',
      },
      {
        name: $i18n('journeyReduceError'),
        content: $i18n('journeyReduceErrorContent'),
        image: 'stageOfProject_reduceError.jpg',
      },
    ] as HomepageJourneyCard[],
);

const data: any = {
  Xometry: 'https://make.3dexperience.3ds.com/lab/xometry?explore=true',
  Sculpteo: 'https://make.3dexperience.3ds.com/lab/sculpteo-villejuif-france',
  'American Additive': 'https://make.3dexperience.3ds.com/lab/american-additive-manufacturing',
  'Get It Made': 'https://make.3dexperience.3ds.com/lab/get-it-made',
  'Any-Shape': 'https://make.3dexperience.3ds.com/lab/any-shape',
  'TH Industries': 'https://make.3dexperience.3ds.com/lab/th-industries',
  '3D Printing': 'https://make.3dexperience.3ds.com/processes/3D-printing',
  'CNC machining': 'https://make.3dexperience.3ds.com/processes/CNC-machining',
  'Injection Molding': 'https://make.3dexperience.3ds.com/processes/injection-molding',
  'Laser Cutting': 'https://make.3dexperience.3ds.com/processes/laser-cutting',
  'Sheet Metal': 'https://make.3dexperience.3ds.com/processes/sheet-metal',
  ABS: 'https://make.3dexperience.3ds.com/materials/generic-plastic-materials',
  'metal materials': 'https://make.3dexperience.3ds.com/materials/metal',
  SOLIDWORKS: 'https://www.solidworks.com/',
  CATIA:
    'https://www.3ds.com/products-services/catia/?wockw=card_content_cta_1_url%3A%22https%3A%2F%2Fblogs.3ds.com%2Fcatia%2F%22',
  Draftsight: 'https://www.draftsight.com/',
  eDrawings: 'https://www.edrawingsviewer.com/',
  emStart: '<em class="make-em">',
  emEnd: '</em>',
};

const translations: any = computed(() => ({
  '3D Printing': $i18nPF('3d_printing'),
  'CNC machining': $i18nPF('cnc_machining'),
  'Injection Molding': $i18nPF('injection_molding'),
  'Laser Cutting': $i18nPF('cutting'),
  'Sheet Metal': $i18nPF('sheet_metal'),
  'metal materials': $i18n('metalMaterials'),
}));

const redirections: any = computed(() => ({
  '3D Printing': '/processes/additive',
  'CNC machining': '/processes/subtractive',
  'Injection Molding': '/processes/molding',
  'Laser Cutting': '/processes/cutting',
  'Sheet Metal': '/processes/sheet-metal',
  'metal materials': '/materials/metal',
  ABS: '/materials/generic-plastic-materials',
  Xometry: 'https://make.3dexperience.3ds.com/lab/xometry?explore=true',
  Sculpteo: 'https://make.3dexperience.3ds.com/lab/sculpteo-villejuif-france',
  'American Additive': 'https://make.3dexperience.3ds.com/lab/american-additive-manufacturing',
  'Get It Made': 'https://make.3dexperience.3ds.com/lab/get-it-made',
  'Any-Shape': 'https://make.3dexperience.3ds.com/lab/any-shape',
  'TH Industries': 'https://make.3dexperience.3ds.com/lab/th-industries',
  SOLIDWORKS: 'https://solidworks.com',
  CATIA:
    'https://www.3ds.com/products-services/catia/?wockw=card_content_cta_1_url%3A%22https%3A%2F%2Fblogs.3ds.com%2Fcatia%2F%22',
  Draftsight: '	https://www.draftsight.com/',
  eDrawings: 'https://www.edrawingsviewer.com/',
}));

const classes: any = {
  'Proto Labs': 'protolabs-link',
  Xometry: 'xometry-link',
  Sculpteo: 'sculpteo-link',
  '3D Systems': 'systems-link',
  '3D Printing': 'printing-link',
  'CNC machining': 'cnc-machining-link',
  'Injection Molding': 'injection-molding-link',
  'Laser Cutting': 'laser-cutting-link',
  'Sheet Metal': 'sheet-metal-link',
  ABS: 'abs-link',
  'metal materials': 'metal-materials-link',
  SOLIDWORKS: 'solidworks-link',
  CATIA: 'catia-link',
  Draftsight: 'draftsight-link',
  eDrawings: 'edrawings-link',
  'American Additive': 'american-additive',
  'Get It Made': 'get-it-made',
  'Any-Shape': 'any-shape',
  'TH Industries': 'th-industries',
};

const prepareLinksForSEO = computed(() =>
  $i18n('ownDesignDesc').replace(/{([^}]+)?}/g, (match, key) =>
    /^em(Start|End)$/.test(key)
      ? data[key]
      : [
          '<a href="',
          redirections.value[key],
          '" class="',
          classes[key] || '',
          '">',
          translations.value[key] || key,
          '</a>',
        ].join(''),
  ),
);
</script>
<template>
  <div class="home-journey-content py-70px py-40px--sm px-10px--sm px-30px">
    <div class="homepage-max-content-container flex flex-column grid-gap-20px">
      <div class="title text-center">
        <i18nSuspense ikey="ownDesignTitle" />
      </div>
      <div class="desc text-content" v-html="prepareLinksForSEO" />
      <div class="grid grid-3-columns grid-2-columns--sm grid-1-columns--xs grid-gap-20px">
        <JourneyCard
          v-for="journey in journeys"
          :key="`${journey.name}-content`"
          :content="journey"
        />
        <div
          class="any-question-card pa-20px flex flex-column align-items-center justify-center text-center grid-gap-20px"
        >
          <div class="have-questions">
            <i18nSuspense ikey="haveAnyQuestions" />
          </div>
          <HomepageBtn
            color="white"
            href="https://www.3ds.com/make/3dexperience-make-contact-us"
            target="_blank"
          >
            <vu-icon icon="mail" />
            <i18nSuspenseCommon ikey="contactUs" />
          </HomepageBtn>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.home-journey-content {
  background-position: bottom;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: #fff;
}

.desc {
  column-count: 3;
  text-align: justify;

  @include variables.for-mid {
    column-count: 2;
  }
  @include variables.for-sm {
    column-count: 1;
  }
}

.any-question-card {
  background-color: var(--blue-2);
}

.any-question-card button {
  height: 60px;
}

.have-questions {
  font-size: 30px;
  color: white;
}
</style>
